import React, { useRef, useState } from "react";
import { Button, ButtonVariant, Link } from "@merge-api/merge-javascript-shared";
import { GET_IN_TOUCH_WITH_TRACKING_PATH } from "../../../../../router/RouterUtils";
import { APICategory } from "../../../../../models/Entities";
import { categoryDisplayName } from "../../../../../models/Helpers";
import { showErrorToast, showSuccessToast } from "../../../../shared/Toasts";
import { SortedModelsMap } from "../CommonModelUtils";
import {
  SCOPE_DISABLED_FIELD_UPGRADE_MESSAGE,
  SCOPE_DISABLED_ORG_MESSAGE,
  SCOPE_DISABLED_REMOTE_UPGRADE_MESSAGE,
} from "./constants";
import SaveScopesDialog from "./components/SaveScopesDialog";
import ScopesDiscardChangesDialog from "./components/ScopesDiscardChangesDialog";
import ScopesEditBar from "./components/ScopesEditBar";
import ScopesTable from "./components/ScopesTable";
import ScopesTableSkeleton from "./components/ScopesTableSkeleton";
import ScopesToolbar from "./components/ScopesToolbar";
import { useScopeEdits } from "./hooks/useScopeEdits";
import { useScopesToolbar } from "./hooks/useScopesToolbar";
import { saveScopeEdits } from "./utils/saveScopeEdits";
import { DependentScopeGroup, fetchDependentScopeGroups } from "./utils/fetchDependentScopeGroups";
import { GENERIC_DEPENDENT_CHECK_ERROR } from "../ScopeConfirmationDialog";

interface Props {
  models: SortedModelsMap;
  setModels: (models: SortedModelsMap) => void;
  category: APICategory;
  isLoading: boolean;
  isViewing: boolean;
  areOptionalScopesEnabled: boolean;
  canToggleFields: boolean;
  canToggleRemoteData: boolean;
  canEnterEdit: boolean;
  disableOrgScopeEditingFlag: boolean;
  onEnterEdit: () => void;
  onExitEdit: () => void;
}

// Plan-gated scope tooltip: the reason + a "Get in touch" CTA that opens the upgrade flow. The link
// is white so it reads on the dark tooltip; the switch's tooltip uses leaveDelay so it's reachable.
const upgradeTooltip = (message: string) => (
  <span>
    {message}.{" "}
    <Link href={GET_IN_TOUCH_WITH_TRACKING_PATH} target="_blank" className="text-white underline">
      Get in touch
    </Link>
  </span>
);

/**
 * Table view for Common Model scopes with batched edits. Toggling collects pending changes;
 * Save flushes them through the existing API and Cancel discards them.
 */
const CommonModelScopes = ({
  models,
  setModels,
  category,
  isLoading,
  isViewing,
  areOptionalScopesEnabled,
  canToggleFields,
  canToggleRemoteData,
  canEnterEdit,
  disableOrgScopeEditingFlag,
  onEnterEdit,
  onExitEdit,
}: Props) => {
  // state
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Dependent models the pending edits cascade to (shown as step 1 of the save dialog), and whether
  // we're currently fetching them after the user clicks Save.
  const [dependentGroups, setDependentGroups] = useState<DependentScopeGroup[]>([]);
  const [checkingDependents, setCheckingDependents] = useState(false);
  // Lets an intentional exit (Cancel / successful Save) navigate without tripping the unsaved-changes
  // guard — reset() hasn't re-rendered editedCount to 0 yet when onExitEdit navigates.
  const allowNavigationRef = useRef(false);

  // hooks
  const { editedModels, edits, editedCount, setModelScope, setFieldScope, reset } = useScopeEdits(
    models,
    areOptionalScopesEnabled,
  );
  const toolbar = useScopesToolbar(category);

  // derived state
  const disableFieldScopes = !canToggleFields || disableOrgScopeEditingFlag;
  const disableRemoteScopes = !canToggleRemoteData || disableOrgScopeEditingFlag;
  // Org-level lockout takes precedence over plan upsell when both apply. Plan-gated tooltips link to
  // the upgrade ("Get in touch") flow; the org-lockout message stays plain text.
  const objectScopesDisabledTooltip = disableOrgScopeEditingFlag
    ? SCOPE_DISABLED_ORG_MESSAGE
    : undefined;
  const fieldScopesDisabledTooltip = disableOrgScopeEditingFlag
    ? SCOPE_DISABLED_ORG_MESSAGE
    : !canToggleFields
    ? upgradeTooltip(SCOPE_DISABLED_FIELD_UPGRADE_MESSAGE)
    : undefined;
  const remoteScopesDisabledTooltip = disableOrgScopeEditingFlag
    ? SCOPE_DISABLED_ORG_MESSAGE
    : !canToggleRemoteData
    ? upgradeTooltip(SCOPE_DISABLED_REMOTE_UPGRADE_MESSAGE)
    : undefined;

  // event handlers
  // Save click: check whether the edits cascade to other models and, if so, open the dialog on its
  // dependents-confirmation step; otherwise open straight to the re-sync step. If the cascade check
  // itself fails, close the dialog and surface the reason instead of opening a confirmation we can't
  // trust.
  const handleRequestSave = async () => {
    setCheckingDependents(true);
    try {
      const groups = await fetchDependentScopeGroups(Object.values(edits), category);
      setDependentGroups(groups);
      setConfirmOpen(true);
    } catch (error) {
      setConfirmOpen(false);
      showErrorToast(
        error instanceof Error && error.message ? error.message : GENERIC_DEPENDENT_CHECK_ERROR,
      );
    } finally {
      setCheckingDependents(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const { models: savedModels, errors } = await saveScopeEdits(
      category,
      models,
      editedModels,
      Object.values(edits),
    );
    setModels(savedModels);
    if (errors.length > 0) {
      // Surface each distinct backend reason (permission, permanently-disabled, structural 400)
      // instead of a single generic toast. Pending edits are kept so the user can correct and retry.
      errors.forEach((message) => showErrorToast(message));
    } else {
      allowNavigationRef.current = true;
      reset();
      // Re-sync the filtered list to the newly saved state (no-op unless a filter is active).
      toolbar.refetch();
      showSuccessToast("Scopes saved");
      setConfirmOpen(false);
      onExitEdit();
    }
    setSaving(false);
  };

  const handleCancel = () => {
    // Only arm the bypass when there's actually something the guard would block, so a no-op cancel
    // doesn't leave it stuck on for the next navigation.
    allowNavigationRef.current = editedCount > 0;
    reset();
    onExitEdit();
  };

  // render
  return (
    <div className="flex flex-col gap-y-3">
      {/* Narrow: toolbar stacks above the Edit/action group (its own line), search goes full-width.
          Wide (lg+): single row with the toolbar left and the Edit/action group right. */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-x-5">
        <ScopesToolbar
          categoryLabel={categoryDisplayName(category)}
          toolbar={toolbar}
          areOptionalScopesEnabled={areOptionalScopesEnabled}
          disabled={!isViewing}
        />
        {isViewing ? (
          canEnterEdit && (
            <Button variant={ButtonVariant.PrimaryGray} onClick={onEnterEdit}>
              Edit
            </Button>
          )
        ) : (
          <ScopesEditBar
            editedCount={editedCount}
            saving={saving || checkingDependents}
            onCancel={handleCancel}
            onSave={handleRequestSave}
          />
        )}
      </div>
      {/* Skeleton while loading AND until the models populate, so the table area never flashes blank
          in the gap where isLoading has flipped false but the fetched models haven't landed yet. */}
      {isLoading || Object.keys(editedModels).length === 0 ? (
        <ScopesTableSkeleton />
      ) : (
        <ScopesTable
          models={editedModels}
          savedModels={models}
          orderedModelNames={toolbar.visibleModelNames}
          resultsLoading={toolbar.resultsLoading}
          category={category}
          isViewing={isViewing}
          areOptionalScopesEnabled={areOptionalScopesEnabled}
          disableObjectScopes={disableOrgScopeEditingFlag}
          disableFieldScopes={disableFieldScopes}
          disableRemoteScopes={disableRemoteScopes}
          objectScopesDisabledTooltip={objectScopesDisabledTooltip}
          fieldScopesDisabledTooltip={fieldScopesDisabledTooltip}
          remoteScopesDisabledTooltip={remoteScopesDisabledTooltip}
          onModelScopeChange={setModelScope}
          onFieldScopeChange={setFieldScope}
        />
      )}
      <SaveScopesDialog
        open={confirmOpen}
        saving={saving}
        dependentGroups={dependentGroups}
        onConfirm={handleSave}
        onClose={() => setConfirmOpen(false)}
      />
      {/* Warn before leaving (chip nav, left nav, back) with unsaved batched edits. Cancel/Save set
          allowNavigationRef so those intentional exits bypass the warning. */}
      <ScopesDiscardChangesDialog
        hasUnsavedChanges={editedCount > 0}
        allowNavigationRef={allowNavigationRef}
      />
    </div>
  );
};

export default CommonModelScopes;
